import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@socialsoup/shared';
import { clearRefreshToken } from '@/lib/auth';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * theme-customization A3 — the SESSION GENERATION.
   *
   * A monotonic in-memory counter identifying "which sign-in is current". It is what lets a
   * callback that started under one account refuse to write anything once another account owns
   * the tab: every asynchronous auth/theme operation captures this value when it STARTS and
   * compares it before it touches the DOM, the query cache, the tokens or navigation.
   *
   * It is deliberately NOT persisted (see `partialize` below, which names the three fields it
   * keeps and nothing else). Persisting it would make a generation survive a reload and let a
   * value from the previous page life compare equal to one in this life — the opposite of what
   * it is for. A reload always starts a fresh in-memory generation with the rehydrated user.
   *
   * Bumped by `beginSession` and by `clearAuth` (which is what logout start calls). An ordinary
   * same-session token refresh goes through `setAuth` and does NOT bump it — a rotated access
   * token is the SAME session, and invalidating every in-flight request on every refresh is
   * exactly the behaviour these guards exist to avoid.
   */
  sessionGeneration: number;
  /** Same-session update — a rotated access token. Never starts a new session. */
  setAuth: (user: User, token: string) => void;
  /**
   * A NEW sign-in: password login or magic-link consume, INCLUDING a same-user re-login.
   * Same-user matters: the user id alone cannot tell "still signed in" from "signed in again",
   * so the generation is what makes a stale callback from the previous sign-in detectable.
   */
  beginSession: (user: User, token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,
      sessionGeneration: 1,

      setAuth: (user, accessToken) =>
        set({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      beginSession: (user, accessToken) =>
        set((state) => ({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
          sessionGeneration: state.sessionGeneration + 1,
        })),

      clearAuth: () => {
        // Owner-scoped: clearing THIS session must not delete a refresh token another tab stored
        // for a different account (A3 security gate).
        clearRefreshToken(useAuthStore.getState().user?.id ?? null);
        set((state) => ({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
          sessionGeneration: state.sessionGeneration + 1,
        }));
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'ss-auth',
      // `sessionGeneration` is absent on purpose — see its doc comment above.
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
